import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { QUIZ } from "../data/content";
import { Sparkles, RotateCcw, ArrowRight, Trophy, X, Zap } from "lucide-react";
import { toast } from "sonner";

const API = `${window.__BACKEND__}/api`;

const DIFFICULTY_COLORS = {
  Facile: "#4fc3ff",
  Media: "#ff4fcf",
  Difficile: "#EA580C",
};

export default function QuizModal({ open, onClose }) {
  const [step, setStep] = useState(0);
  const [started, setStarted] = useState(false);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const totalQuestions = QUIZ.length;
  const progress = ((step + (started ? 1 : 0)) / totalQuestions) * 100;

  const answer = (qid, val) => {
    const next = { ...answers, [qid]: val };
    setAnswers(next);
    if (step + 1 < totalQuestions) {
      setTimeout(() => setStep(step + 1), 280);
    } else {
      submit(next);
    }
  };

  const submit = async (final) => {
    setLoading(true);
    try {
      const payload = {
        answers: Object.entries(final).map(([qid, v]) => ({
          question_id: Number(qid),
          answer_value: v,
        })),
      };
      const res = await axios.post(`${API}/quiz/submit`, payload);
      setResult(res.data);
    } catch {
      toast.error("Errore quiz, riprova");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(0);
    setAnswers({});
    setResult(null);
    setStarted(false);
  };

  const close = () => {
    onClose();
    // defer reset so we don't show the reset during close animation
    setTimeout(reset, 400);
  };

  const q = QUIZ[step];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-xl overflow-y-auto"
          data-testid="quiz-modal"
        >
          {/* Ambient glow */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full bg-[var(--vdh-pink)]/14 blur-[180px]" />
            <div className="absolute bottom-1/4 right-1/4 w-[600px] h-[600px] rounded-full bg-[var(--vdh-yellow)]/12 blur-[180px]" />
          </div>

          <button
            onClick={close}
            data-testid="close-quiz-modal"
            className="fixed top-5 right-5 z-50 w-11 h-11 rounded-full glass flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-all"
            aria-label="Chiudi"
          >
            <X size={20} />
          </button>

          <div className="relative min-h-screen flex items-center justify-center px-5 py-16">
            <div className="w-full max-w-4xl">
              {/* INTRO */}
              {!started && !result && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="text-center"
                >
                  <div
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
                    style={{
                      background:
                        "linear-gradient(90deg, rgba(255,79,207,0.15), rgba(234,88,12,0.15))",
                      border: "1px solid rgba(255,79,207,0.35)",
                    }}
                  >
                    <Zap size={14} className="text-[var(--vdh-yellow)]" />
                    <span className="text-[11px] uppercase tracking-[0.25em] text-white/85 font-tech">
                      Inizia da qui
                    </span>
                  </div>

                  <h2 className="font-display text-5xl sm:text-6xl md:text-7xl leading-[0.92] mb-5 tracking-tight">
                    Che tipo di <span className="neon-pink">Host</span>
                    <br />
                    o <span className="neon-yellow">PM</span> sei?
                  </h2>
                  <p className="text-white/60 text-base md:text-lg mb-8 max-w-2xl mx-auto">
                    10 domande basate sul manuale <strong className="text-white">FaqBnb 2026</strong>.
                    In 60 secondi scopri il tuo livello e ricevi il percorso Vita da Host su misura per te.
                  </p>

                  <div className="flex flex-wrap gap-6 justify-center mb-10 text-center">
                    <div>
                      <div className="font-display text-3xl md:text-4xl neon-pink">10</div>
                      <div className="text-[10px] uppercase tracking-widest text-white/45 font-tech">Domande</div>
                    </div>
                    <div className="w-px bg-white/10" />
                    <div>
                      <div className="font-display text-3xl md:text-4xl neon-yellow">3</div>
                      <div className="text-[10px] uppercase tracking-widest text-white/45 font-tech">Livelli</div>
                    </div>
                    <div className="w-px bg-white/10" />
                    <div>
                      <div className="font-display text-3xl md:text-4xl text-white">60s</div>
                      <div className="text-[10px] uppercase tracking-widest text-white/45 font-tech">Durata</div>
                    </div>
                  </div>

                  <button
                    onClick={() => setStarted(true)}
                    className="pill-btn pill-btn-primary text-base md:text-lg px-8 py-4"
                    data-testid="quiz-start"
                  >
                    <Sparkles size={16} /> Inizia il quiz <ArrowRight size={16} />
                  </button>
                  <div className="mt-4 text-[11px] text-white/35 uppercase tracking-widest font-tech">
                    Gratis · senza registrazione
                  </div>
                </motion.div>
              )}

              {/* QUESTIONS */}
              {started && !result && q && (
                <div className="max-w-3xl mx-auto">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="text-xs font-tech uppercase tracking-[0.25em] text-white/50">
                        Domanda {step + 1} / {totalQuestions}
                      </div>
                      <span
                        className="text-[10px] uppercase tracking-widest font-tech px-2 py-0.5 rounded-full"
                        style={{
                          color: DIFFICULTY_COLORS[q.difficulty],
                          background: `${DIFFICULTY_COLORS[q.difficulty]}18`,
                          border: `1px solid ${DIFFICULTY_COLORS[q.difficulty]}55`,
                        }}
                      >
                        {q.difficulty}
                      </span>
                    </div>
                    <button
                      onClick={reset}
                      className="text-xs text-white/40 hover:text-white flex items-center gap-1.5"
                      data-testid="quiz-reset"
                    >
                      <RotateCcw size={12} /> ricomincia
                    </button>
                  </div>
                  <div className="h-1 bg-white/5 rounded-full overflow-hidden mb-8">
                    <motion.div
                      className="h-full bg-gradient-to-r from-[var(--vdh-pink)] to-[var(--vdh-yellow)]"
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>

                  <AnimatePresence mode="wait">
                    <motion.div
                      key={q.id}
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -30 }}
                      transition={{ duration: 0.35 }}
                    >
                      <h3 className="font-display text-2xl md:text-4xl leading-tight mb-7 tracking-tight">
                        {q.q}
                      </h3>
                      <div className="space-y-3">
                        {q.options.map((o, i) => (
                          <button
                            key={i}
                            onClick={() => answer(q.id, o.v)}
                            className="w-full text-left glass glass-hover rounded-2xl px-5 py-4 flex items-center justify-between group transition-all hover:translate-x-1"
                            data-testid={`quiz-q${q.id}-opt${i}`}
                          >
                            <span className="flex items-center gap-3 text-sm md:text-base">
                              <span className="w-7 h-7 rounded-full border border-white/15 flex items-center justify-center text-xs font-tech text-white/60 group-hover:border-[var(--vdh-pink)] group-hover:text-[var(--vdh-pink)] transition-colors">
                                {String.fromCharCode(65 + i)}
                              </span>
                              {o.text}
                            </span>
                            <ArrowRight
                              size={18}
                              className="text-white/30 group-hover:text-[var(--vdh-pink)] transition-colors"
                            />
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>
              )}

              {/* LOADING */}
              {loading && (
                <div className="text-center py-16 text-white/60">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }}
                    className="inline-block mb-3"
                  >
                    <Sparkles size={32} className="text-[var(--vdh-pink)]" />
                  </motion.div>
                  <div className="font-tech">Analizziamo il tuo profilo…</div>
                </div>
              )}

              {/* RESULT */}
              {result && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="text-center"
                  data-testid="quiz-result"
                >
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--vdh-yellow)] text-black text-xs font-tech uppercase tracking-[0.25em] mb-5">
                    <Trophy size={14} /> Il tuo profilo
                  </div>
                  <h3 className="font-display text-5xl md:text-7xl leading-[0.92] mb-3 tracking-tight">
                    Sei <span className="neon-pink">
                      {result.profile.split("|")[1] || result.profile}
                    </span>
                  </h3>
                  <div className="text-sm text-white/50 font-tech mb-8">
                    Punteggio: <span className="text-white">{result.score}</span> / 20
                  </div>
                  <p className="text-white/60 text-base md:text-lg mb-10 max-w-2xl mx-auto">
                    Ecco i percorsi selezionati per il tuo livello, tutti interni a Vita da Host.
                  </p>

                  <div
                    className={`grid ${result.recommended_links.length >= 4 ? "md:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-3"} gap-4 text-left`}
                  >
                    {result.recommended_links.map((r, i) => {
                      const isInternal = typeof r.url === "string" && r.url.startsWith("/");
                      const cardContent = (
                        <>
                          <div className="text-[10px] font-tech uppercase tracking-widest text-[var(--vdh-pink)] mb-3">
                            Consigliato 0{i + 1}
                          </div>
                          <h4 className="font-display text-xl md:text-2xl mb-3 leading-tight tracking-tight">
                            {r.title}
                          </h4>
                          <p className="text-white/55 text-sm mb-5">{r.description}</p>
                          <div className="inline-flex items-center gap-2 text-sm font-tech text-[var(--vdh-yellow)] group-hover:gap-3 transition-all">
                            Vai al percorso <ArrowRight size={14} />
                          </div>
                        </>
                      );
                      const classes = "glass glass-hover rounded-3xl p-6 group block";
                      return (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.12 + 0.3 }}
                          data-testid={`quiz-result-link-${i}`}
                        >
                          {isInternal ? (
                            <Link to={r.url} onClick={close} className={classes}>
                              {cardContent}
                            </Link>
                          ) : (
                            <a href={r.url} target="_blank" rel="noopener noreferrer" className={classes}>
                              {cardContent}
                            </a>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>

                  <button onClick={reset} className="pill-btn mt-10" data-testid="quiz-retake">
                    <RotateCcw size={14} /> Rifai il quiz
                  </button>
                </motion.div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
